<template>
  <div>
    <div class="new-main-page">
      <h1 class="new-main-header">
        <img
          :src="theme.current.headerImage"
          alt="AI Logo"
          class="header-image"
        />
      </h1>
      <div class="content">
        <!-- <router-link to="#" @click="goBack" class="back-link"> &lt; Back</router-link> -->
        <div class="page-title-row">
          <h4 class="form-title">User Management</h4>
          <AdminHelpButton
            class="page-help-button"
            floating-width="430px"
            presentation="floating"
            :topic="USER_MANAGEMENT_HELP"
          />
        </div>
        <div class="actions">
          <input
            type="text"
            v-model="searchQuery"
            placeholder="Search users..."
            class="search-input"
            @input="onSearchInput"
            @keyup.enter="searchForUsers"
          />
          <button @click="searchForUsers" class="show-form-button">
            Search
          </button>
          <button @click="showForm" class="show-form-button">Add User +</button>
          <button @click="showBulkUploadForm" class="show-form-button">
            Add/Update Users
          </button>
          <button
            v-if="session.rolesIncludes('admin')"
            @click="showBulkArchiveForm"
            class="show-form-button"
          >
            Bulk Archive
          </button>
          <!-- Off by default: archived accounts are kept for their history and
               would otherwise pad every search with rows nobody wants. -->
          <label
            class="archive-toggle"
            :class="{ 'archive-toggle--on': includeArchived }"
            title="Show accounts that were archived to free their email address"
          >
            <span class="switch switch--sm">
              <input
                type="checkbox"
                v-model="includeArchived"
                @change="toggleIncludeArchived"
              />
              <span class="slider round"></span>
            </span>
            <span class="archive-toggle-text">
              <i class="fas fa-archive"></i>
              Include archived
            </span>
          </label>
          <!-- <select v-model="sortKey" class="sort-dropdown">
                    <option value="" disabled >All</option>
                    <option value="userId">User Id</option>
                    <option value="userGroup">User Group</option>
                    <option value="email">Email</option>
                    <option value="userTypes">User Type</option>
                    <option value="createDate">Create Date</option>
                  </select> -->
        </div>
        <div v-if="isFormVisible" class="overlay">
          <div class="overlay-content">
            <div>
              <button @click="hideForm" class="close-button">
                <i class="fas fa-times"></i>
              </button>
              <h2 class="form-header">
                {{ isEditing ? "Edit User" : "Add User" }}
              </h2>
            </div>
            <hr />
            <form @submit.prevent="submitForm">
              <div class="form-group">
                <input
                  type="text"
                  id="userId"
                  v-model="form.userId"
                  placeholder="User ID"
                  disabled
                />
              </div>
              <div class="form-group">
                <div class="group-select-wrapper">
                  <select
                    v-model="form.userGroup"
                    @mousedown="fetchGroupsIfNeeded"
                    @focus="fetchGroupsIfNeeded"
                    :aria-busy="groupsLoading ? 'true' : 'false'"
                  >
                    <option value="" disabled>Group *</option>
                    <option v-if="groupsLoading" disabled>Loading groups…</option>
                    <option v-else-if="groupsError" disabled>
                      {{ groupsError }}
                    </option>
                    <option
                      v-else
                      v-for="g in groups"
                      :key="gKey(g)"
                      :value="gValue(g)"
                    >
                      {{ gLabel(g) }}
                    </option>
                  </select>
                  <small class="field-hint">
                    <code>military0</code> = military (undecided year)<br />
                    <code>military</code> (no number) is deprecated
                  </small>
                </div>
              </div>
              <div class="form-group">
                <input
                  type="text"
                  id="firstName"
                  v-model="form.firstName"
                  placeholder="First Name"
                />
                <input
                  type="text"
                  id="lastName"
                  v-model="form.lastName"
                  placeholder="Last Name"
                />
              </div>
              <div class="form-group">
                <input
                  type="email"
                  id="email"
                  v-model="form.email"
                  :placeholder="isEditing ? 'Email' : 'Email *'"
                  required
                  :disabled="isEditing"
                />
                <input
                  type="tel"
                  id="phone"
                  v-model="form.phone"
                  placeholder="+1##########"
                />
              </div>
              <!-- <div class="form-group">
                        <input type="text" id="username" v-model="form.username" placeholder="Username">
                      </div> -->
              <div class="form-group">
                <input
                  type="password"
                  id="password"
                  v-model="form.password"
                  placeholder="Password"
                />
                <input
                  type="password"
                  id="confirmPassword"
                  v-model="form.confirmPassword"
                  placeholder="Confirm Password"
                />
              </div>
              <div class="form-group group-opt">
                <label class="opt-label">
                  <input
                    type="checkbox"
                    v-model="form.smsOptIn"
                    class="opt-checkbox"
                  />
                  <span class="opt-text"> Receive SMS reminders </span>
                </label>
                <label class="opt-label">
                  <input
                    type="checkbox"
                    v-model="form.emailOptIn"
                    class="opt-checkbox"
                  />
                  <span class="opt-text"> Receive email reminders </span>
                </label>
                <label class="opt-label">
                  <input
                    type="checkbox"
                    v-model="form.enableSessionManipulation"
                    class="opt-checkbox"
                  />
                  <span class="opt-text"> Enable Session Manipulation </span>
                </label>
                <label class="opt-label">
                  <input
                    type="checkbox"
                    v-model="form.runSummarization"
                    class="opt-checkbox"
                  />
                  <span class="opt-text"> Automate summarization </span>
                </label>
                <label class="opt-label">
                  <input
                    type="checkbox"
                    v-model="form.preSurvey"
                    class="opt-checkbox"
                  />
                  <span class="opt-text"> Pre-survey </span>
                </label>
                <label class="opt-label">
                  <input
                    type="checkbox"
                    v-model="form.postSurvey"
                    class="opt-checkbox"
                  />
                  <span class="opt-text"> Post-survey </span>
                </label>
                <label class="opt-label">
                  <input
                    type="checkbox"
                    v-model="form.phoneVerified"
                    class="opt-checkbox"
                    disabled
                  />
                  <span class="opt-text"> Phone verified </span>
                </label>
                <label class="opt-label">
                  <input
                    type="checkbox"
                    v-model="form.phoneConfirmed"
                    class="opt-checkbox"
                  />
                  <span class="opt-text"> Phone confirmed </span>
                </label>
                <label class="opt-label">
                  <input
                    type="checkbox"
                    v-model="form.disableUser"
                    class="opt-checkbox"
                  />
                  <span class="opt-text"> Disable user </span>
                </label>
                <label class="opt-label">
                  <input
                    type="checkbox"
                    v-model="form.sendOnboardingEmail"
                    class="opt-checkbox"
                    :disabled="isEditing"
                  />
                  <span class="opt-text"> Send onboarding email </span>
                </label>
              </div>
              <div v-if="!passwordsMatch" class="error-message">
                Passwords do not match
              </div>
              <div v-if="phoneRequiredInvalid" class="error-message">
                Phone must start with "+1" and contain 10 digits.
              </div>
              <div v-else class="error-message">&nbsp;</div>
              <h3 class="user-type-header">User Type *</h3>
              <!-- div v-if="!userTypeSelected" class="error-message">User Type is Required</div -->
              <div class="checkbox-group">
                <label
                  ><input
                    type="checkbox"
                    v-model="form.userTypes"
                    value="admin"
                  />
                  Admin</label
                >
                <label
                  ><input
                    type="checkbox"
                    v-model="form.userTypes"
                    value="mentor"
                  />
                  Peer Mentor</label
                >
                <label
                  ><input
                    type="checkbox"
                    v-model="form.userTypes"
                    value="mentee"
                  />
                  Peer Mentee</label
                >
                <label
                  ><input
                    type="checkbox"
                    v-model="form.userTypes"
                    value="user"
                  />
                  User</label
                >
              </div>
              <div class="button-container">
                <button
                  type="submit"
                  class="submit-button"
                  :disabled="
                    !passwordsMatch ||
                    !userTypeSelected ||
                    !userGroupSelected ||
                    phoneRequiredInvalid
                  "
                >
                  Submit
                </button>
                <button type="button" @click="hideForm" class="cancel-button">
                  Cancel
                </button>
              </div>
            </form>

            <!-- Archiving frees a taken address for a new signup. Kept out of
                 the form above because it is not an edit: it saves on its own,
                 and the email input stays read-only. -->
            <div
              v-if="isEditing && session.rolesIncludes('admin')"
              class="archive-section"
            >
              <h3 class="user-type-header">Archive</h3>

              <div v-if="archiveInfoLoading" class="archive-card">
                <div class="archive-card-body">
                  <i class="fas fa-spinner fa-spin archive-icon"></i>
                  <div class="archive-copy">
                    <p class="archive-desc">Checking archive status...</p>
                  </div>
                </div>
              </div>

              <div
                v-else-if="archiveInfo"
                class="archive-card"
                :class="{ 'archive-card--archived': archiveInfo.is_archived }"
              >
                <!-- Live account: offer to free its address. -->
                <div v-if="!archiveInfo.is_archived" class="archive-card-body">
                  <i class="fas fa-archive archive-icon"></i>
                  <div class="archive-copy">
                    <p class="archive-title">Free this address</p>
                    <p class="archive-desc">
                      Moves this account to an archive address and deactivates
                      it, keeping its sessions and summaries.
                      <b>{{ form.email }}</b> then becomes available for a new
                      account, which you create separately.
                    </p>
                  </div>
                  <button
                    type="button"
                    class="archive-button"
                    :disabled="archiveBusy"
                    @click="archiveUser"
                  >
                    <i v-if="archiveBusy" class="fas fa-spinner fa-spin"></i>
                    {{ archiveBusy ? "Archiving" : "Archive" }}
                  </button>
                </div>

                <!-- Archived account: offer to put it back. -->
                <div v-else class="archive-card-body">
                  <i class="fas fa-box-open archive-icon"></i>
                  <div class="archive-copy">
                    <p class="archive-title">
                      <span class="archived-badge">Archived</span>
                      <span class="archive-date">{{
                        archiveInfo.archived_at
                      }}</span>
                    </p>
                    <p class="archive-desc">
                      Originally registered as
                      <b>{{ archiveInfo.original_email }}</b
                      >. Unarchiving returns it to that address and makes it
                      active again.
                    </p>
                    <!-- The holder's address is the original address, so naming
                         both would read "X is held by X". The account id and its
                         creation date are what identify the holder. -->
                    <p v-if="!archiveInfo.can_unarchive" class="archive-blocked">
                      <i class="fas fa-exclamation-triangle"></i>
                      <span>
                        <b>{{ archiveInfo.original_email }}</b> is currently held
                        by
                        <b
                          >user #{{
                            archiveInfo.original_email_holder?.user_id
                          }}</b
                        >, created
                        {{ archiveInfo.original_email_holder?.created_at }}.
                        Archive that account first to free the address.
                      </span>
                    </p>
                  </div>
                  <button
                    type="button"
                    class="archive-button archive-button--restore"
                    :disabled="archiveBusy || !archiveInfo.can_unarchive"
                    :title="
                      archiveInfo.can_unarchive
                        ? 'Return this account to its original address'
                        : `${archiveInfo.original_email} is currently held by another account`
                    "
                    @click="unarchiveUser"
                  >
                    <i v-if="archiveBusy" class="fas fa-spinner fa-spin"></i>
                    {{ archiveBusy ? "Unarchiving" : "Unarchive" }}
                  </button>
                </div>

                <!-- Everything archived away from the same address. -->
                <div v-if="archiveInfo.archives.length" class="archive-history">
                  <button
                    type="button"
                    class="archive-disclosure"
                    :aria-expanded="archiveListExpanded ? 'true' : 'false'"
                    @click="archiveListExpanded = !archiveListExpanded"
                  >
                    <i
                      class="fas"
                      :class="
                        archiveListExpanded
                          ? 'fa-chevron-up'
                          : 'fa-chevron-down'
                      "
                    ></i>
                    {{ archiveInfo.archives.length }} archived
                    {{
                      archiveInfo.archives.length === 1 ? "account" : "accounts"
                    }}
                    previously registered with
                    {{ archiveInfo.original_email }}
                  </button>
                  <ul v-if="archiveListExpanded" class="archive-list">
                    <li
                      v-for="row in archiveInfo.archives"
                      :key="row.user_id"
                      class="archive-list-row"
                    >
                      <span class="archive-list-email">{{ row.email }}</span>
                      <span class="archive-list-meta">
                        {{ row.archived_at || "date unknown" }}
                        <template v-if="row.archived_by">
                          &middot; {{ row.archived_by }}
                        </template>
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="isBulkUploadVisible" class="overlay">
          <div class="overlay-content bulk-upload-overlay">
            <div>
              <button @click="hideBulkUploadForm" class="close-button">
                <i class="fas fa-times"></i>
              </button>
              <h2 class="form-header">Bulk Upload Users</h2>
            </div>
            <hr />

            <!-- CSV Upload Section -->
            <div class="upload-section">
              <div class="upload-instructions">
                <h3>CSV File Requirements</h3>
                <p class="mi-note">
                  Each row is one user. Header row required.
                </p>

                <ul class="mi-list">
                  <li>
                    <span class="mi-badge mi-badge--req">Required</span>
                    <strong>Group name</strong>: must match an existing group
                    (e.g., <code>civilian</code>, <code>military1</code>-<code>military4</code>;
                    use <code>military0</code> for undecided military year).
                    <code>military</code> (no number) is deprecated.
                  </li>
                  <li>
                    <span class="mi-badge">Optional</span>
                    <strong>First name</strong>: e.g., <code>John</code>
                  </li>
                  <li>
                    <span class="mi-badge">Optional</span>
                    <strong>Last name</strong>: e.g., <code>Doe</code>
                  </li>
                  <li>
                    <span class="mi-badge mi-badge--req">Required</span>
                    <strong>Email</strong>: e.g.,
                    <code>example-user@example.com</code>
                  </li>
                  <li>
                    <span class="mi-badge mi-badge--req">Required</span>
                    <strong>Password</strong>: e.g.,
                    <code>examplepassword</code>
                  </li>
                  <li>
                    <span class="mi-badge mi-badge--req">Required</span>
                    <strong>User types</strong>: semicolon-separated list (e.g.,
                    <code>admin;mentor</code>). Allowed: <code>admin</code>,
                    <code>mentor</code>, <code>mentee</code>, <code>user</code>
                  </li>

                  <li>
                    <span class="mi-badge">Optional</span>
                    <strong>Phone number</strong>: country code + 10 digits
                    (e.g., <code>+11234567890</code>)
                  </li>
                  <li>
                    <span class="mi-badge">Optional</span>
                    <strong>Receive SMS reminders</strong>:
                    <code>true</code>/<code>false</code>
                    <em>(default: false)</em>
                  </li>
                  <li>
                    <span class="mi-badge">Optional</span>
                    <strong>Receive email reminders</strong>:
                    <code>true</code>/<code>false</code>
                    <em>(default: false)</em>
                  </li>
                  <li>
                    <span class="mi-badge">Optional</span>
                    <strong>Enable session manipulation</strong>:
                    <code>true</code>/<code>false</code>
                    <em>(default: false)</em>
                  </li>
                  <li>
                    <span class="mi-badge">Optional</span>
                    <strong>Automate summarization</strong>:
                    <code>true</code>/<code>false</code>
                    <em>(default: true)</em>
                  </li>
                  <li>
                    <span class="mi-badge">Optional</span>
                    <strong>Pre-survey</strong>: <code>true</code>/<code
                      >false</code
                    >
                    <em>(default: true)</em>
                  </li>
                  <li>
                    <span class="mi-badge">Optional</span>
                    <strong>Post-survey</strong>: <code>true</code>/<code
                      >false</code
                    >
                    <em>(default: true)</em>
                  </li>
                  <li>
                    <span class="mi-badge">Optional</span>
                    <strong>Phone confirmed</strong>: <code>true</code>/<code>false</code>
                    — set to <code>false</code> to send military users back through phone
                    verification and group selection
                  </li>
                  <li>
                    <span class="mi-badge">Optional</span>
                    <strong>Is active</strong>: <code>true</code>/<code
                      >false</code
                    >
                    <em>(default: true)</em>
                  </li>
                </ul>

                <p class="mi-note">
                  Tips: boolean values accept <code>true/false</code>,
                  <code>yes/no</code>, or <code>1/0</code>.
                </p>
              </div>
              <label class="opt-label-bulk opt-label--singleline">
                <input
                  type="checkbox"
                  v-model="bulkUpdateMode"
                  class="opt-checkbox"
                />
                <span class="opt-text">
                  Update existing users (match by email) instead of creating new
                  ones
                </span>
              </label>
              <p class="mi-note" style="margin-top: 4px">
                In update mode, rows are matched to existing accounts by email.
                No new users are created and no onboarding emails are sent.
              </p>
              <label class="opt-label-bulk opt-label--singleline">
                <input
                  type="checkbox"
                  v-model="bulkSendNotifications"
                  class="opt-checkbox"
                  :disabled="bulkUpdateMode"
                />
                <span class="opt-text">
                  Send onboarding emails to newly created accounts
                </span>
              </label>
              <p style="font-size: 12px">
                This sends a one-time onboarding email per account after a
                successful bulk create.
              </p>
              <div class="file-upload-container">
                <input
                  type="file"
                  id="csvFile"
                  ref="csvFileInput"
                  accept=".csv"
                  @change="handleFileSelect"
                  class="file-input"
                />
                <label for="csvFile" class="file-upload-label">
                  <i class="fas fa-upload"></i>
                  {{ selectedFile ? selectedFile.name : "Choose CSV File" }}
                </label>
              </div>

              <!-- Preview Section -->
              <div v-if="csvPreview.length > 0" class="preview-section">
                <h3>Preview (First 5 rows):</h3>
                <div class="preview-table-container">
                  <table class="preview-table">
                    <thead>
                      <tr>
                        <th v-for="header in csvHeaders" :key="header">
                          {{ header }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(row, index) in csvPreview.slice(0, 5)"
                        :key="index"
                      >
                        <td v-for="header in csvHeaders" :key="header">
                          {{ row[header] }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p class="preview-info">
                  Total rows to upload: {{ csvPreview.length }}
                </p>
              </div>

              <!-- Error Messages -->
              <div v-if="bulkUploadError" class="error-message">
                {{ bulkUploadError }}
              </div>

              <!-- Success Message -->
              <div v-if="bulkUploadSuccess" class="success-message">
                {{ bulkUploadSuccess }}
              </div>
            </div>

            <div class="button-container">
              <button
                type="button"
                @click="processBulkUpload"
                class="submit-button"
                :disabled="
                  !selectedFile ||
                  csvPreview.length === 0 ||
                  isUploading ||
                  isUploadLocked
                "
              >
                <i v-if="isUploading" class="fas fa-spinner fa-spin"></i>
                {{
                  isUploading
                    ? "Uploading..."
                    : bulkUpdateMode
                    ? "Upload Updates"
                    : bulkSendNotifications
                    ? "Upload & Notify"
                    : "Upload Users"
                }}
              </button>
              <button
                type="button"
                @click="hideBulkUploadForm"
                class="cancel-button"
              >
                Cancel
              </button>
            </div>
            <div v-if="isUploadLocked" class="mi-note">
              Upload complete. Load a new CSV to upload again.
            </div>
          </div>
        </div>
        <!-- Bulk archive. Kept separate from Add/Update Users because it is a
             different operation on a different set of accounts: that form
             describes the accounts to create, this one names the accounts
             standing in their way. -->
        <div v-if="isBulkArchiveVisible" class="overlay">
          <div class="overlay-content bulk-upload-overlay">
            <div>
              <button @click="hideBulkArchiveForm" class="close-button">
                <i class="fas fa-times"></i>
              </button>
              <h2 class="form-header">Bulk Archive</h2>
            </div>
            <hr />

            <div class="upload-section">
              <div class="upload-instructions">
                <p class="mi-note">
                  Upload a CSV with an <code>Email</code> column. Every other
                  column is ignored, so the same roster file you use for
                  Add/Update Users works unchanged.
                </p>
                <p class="mi-note">
                  Archiving frees each address for a new account and deactivates
                  the old one. Nothing is created here.
                </p>
              </div>

              <div class="bulk-archive-mode">
                <label
                  class="bulk-archive-mode-option"
                  :class="{ 'is-selected': bulkArchiveMode === 'archive' }"
                >
                  <input
                    type="radio"
                    value="archive"
                    v-model="bulkArchiveMode"
                    @change="invalidateBulkArchivePlan"
                  />
                  <i class="fas fa-archive"></i>
                  <span>
                    <b>Archive</b>
                    <small>Free these addresses for new accounts</small>
                  </span>
                </label>
                <label
                  class="bulk-archive-mode-option"
                  :class="{ 'is-selected': bulkArchiveMode === 'unarchive' }"
                >
                  <input
                    type="radio"
                    value="unarchive"
                    v-model="bulkArchiveMode"
                    @change="invalidateBulkArchivePlan"
                  />
                  <i class="fas fa-box-open"></i>
                  <span>
                    <b>Unarchive</b>
                    <small>Return archived accounts to their addresses</small>
                  </span>
                </label>
              </div>

              <input
                type="file"
                accept=".csv"
                class="bulk-archive-file"
                @change="handleBulkArchiveFile"
              />
              <p v-if="bulkArchiveEmails.length" class="archive-note">
                {{ bulkArchiveEmails.length }} address{{
                  bulkArchiveEmails.length === 1 ? "" : "es"
                }}
                read from {{ bulkArchiveFile && bulkArchiveFile.name }}.
              </p>
              <div v-if="bulkArchiveError" class="error-message">
                {{ bulkArchiveError }}
              </div>

              <!-- Not .button-container: that one right-aligns with no gap,
                   which would run these three together. -->
              <div class="bulk-archive-actions">
                <button
                  type="button"
                  class="show-form-button"
                  :disabled="bulkArchiveEmails.length === 0 || bulkArchiveBusy"
                  @click="runBulkArchive(true)"
                >
                  <i v-if="bulkArchiveBusy" class="fas fa-spinner fa-spin"></i>
                  Dry run
                </button>
                <button
                  type="button"
                  class="archive-button"
                  :disabled="!canExecuteBulkArchive"
                  :title="
                    bulkArchivePlan
                      ? 'Apply the previewed changes'
                      : 'Run a dry run first'
                  "
                  @click="runBulkArchive(false)"
                >
                  {{ bulkArchiveModeLabel }}
                  {{ bulkArchivePlanActionable || "" }}
                </button>
                <button
                  v-if="bulkArchiveTable"
                  type="button"
                  class="archive-button bulk-archive-export"
                  @click="exportBulkArchiveResults"
                >
                  <i class="fas fa-download"></i> Export CSV
                </button>
              </div>

              <!-- Results, for both phases. -->
              <div v-if="bulkArchiveTable" class="bulk-archive-results">
                <p class="archive-note">
                  <b>{{ bulkArchiveTable.message }}</b>
                </p>
                <div class="bulk-archive-summary">
                  <span
                    v-for="chip in bulkArchiveSummaryChips"
                    :key="chip.key"
                    class="summary-chip"
                    :class="`summary-chip--${chip.key}`"
                  >
                    {{ chip.count }} {{ chip.label }}
                  </span>
                </div>
                <table class="bulk-archive-table">
                  <thead>
                    <tr>
                      <th>Address</th>
                      <th>Outcome</th>
                      <th>Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="row in bulkArchiveTable.rows"
                      :key="row.index"
                      :class="{ 'row-drifted': bulkArchiveRowDrifted(row) }"
                    >
                      <td>{{ row.email }}</td>
                      <td>
                        <span
                          class="summary-chip"
                          :class="`summary-chip--${row.outcome}`"
                          >{{ row.outcome.replace("_", " ") }}</span
                        >
                      </td>
                      <td>
                        <span v-if="row.result_email">{{
                          row.result_email
                        }}</span>
                        <span v-else class="archive-list-meta">{{
                          row.detail
                        }}</span>
                        <div
                          v-if="bulkArchiveRowDrifted(row)"
                          class="drift-note"
                        >
                          <i class="fas fa-exclamation-triangle"></i>
                          preview said {{ bulkArchivePlannedEmail(row) }}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <!-- Delete confirmation modal -->
        <div v-if="isDeleteConfirmVisible" class="overlay">
          <div class="overlay-content confirm-overlay-content">
            <button @click="cancelDelete" class="close-button">
              <i class="fas fa-times"></i>
            </button>
            <h2 class="form-header">Confirm Deletion</h2>
            <hr />
            <p>
              You're about to delete this user account:
              <strong>{{ deleteTarget.email }}</strong>
              <span v-if="deleteTarget.userId">
                (ID: {{ deleteTarget.userId }})</span
              >.
            </p>
            <p>This action cannot be undone.</p>

            <div class="confirm-instructions">
              Type the user's email to confirm:
            </div>
            <input
              type="text"
              v-model.trim="deleteConfirmText"
              placeholder="user@example.com"
              class="confirm-input"
              @keyup.enter="canConfirmDelete ? confirmDelete() : null"
            />

            <div v-if="deleteError" class="error-message">
              {{ deleteError }}
            </div>
            <div v-if="deleteSuccess" class="success-message">
              {{ deleteSuccess }}
            </div>

            <div class="button-container">
              <button
                type="button"
                class="submit-button"
                :disabled="!canConfirmDelete || isDeleting"
                @click="confirmDelete"
              >
                <i v-if="isDeleting" class="fas fa-spinner fa-spin"></i>
                {{ isDeleting ? "Deleting..." : "Delete" }}
              </button>
              <button type="button" class="cancel-button" @click="cancelDelete">
                Cancel
              </button>
            </div>
          </div>
        </div>
        <!-- Resend onboarding email modal -->
        <div v-if="isResendModalVisible" class="overlay">
          <div class="overlay-content confirm-overlay-content">
            <button @click="cancelResend" class="close-button">
              <i class="fas fa-times"></i>
            </button>
            <h2 class="form-header">Resend Onboarding Email</h2>
            <hr />
            <p>
              This resets the login password for
              <strong>{{ resendTarget.email }}</strong>
              and re-sends the onboarding email with the new password.
            </p>

            <div class="form-group">
              <input
                type="password"
                v-model="resendPassword"
                placeholder="New Password"
                class="confirm-input"
              />
              <input
                type="password"
                v-model="resendConfirmPassword"
                placeholder="Confirm New Password"
                class="confirm-input"
                @keyup.enter="canConfirmResend ? confirmResend() : null"
              />
            </div>
            <div
              v-if="resendConfirmPassword && !resendPasswordsMatch"
              class="error-message"
            >
              Passwords do not match
            </div>

            <div v-if="resendError" class="error-message">
              {{ resendError }}
            </div>

            <div class="button-container">
              <button
                type="button"
                class="submit-button"
                :disabled="!canConfirmResend || resendBusy"
                @click="confirmResend"
              >
                <i v-if="resendBusy" class="fas fa-spinner fa-spin"></i>
                {{ resendBusy ? "Sending..." : "Reset & Resend" }}
              </button>
              <button type="button" class="cancel-button" @click="cancelResend">
                Cancel
              </button>
            </div>
          </div>
        </div>
        <!-- <h2 class="form-title">List Users</h2> -->
        <div class="data-table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th @click="sortBy('userId')">User Id</th>
                <th @click="sortBy('email')">Email</th>
                <th @click="sortBy('userTypes')">User Type</th>
                <th @click="sortBy('userGroup')">User Group</th>
                <th @click="sortBy('createDate')">Create Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <!-- tr v-for="user in paginatedUsers" :key="user.userId" -->
              <tr v-for="user in users" :key="user.userId">
                <td>{{ user.userId }}</td>
                <td>
                  {{ user.email }}
                  <span v-if="user.archivedAt" class="archived-badge"
                    >archived</span
                  >
                </td>
                <td>{{ formatUserTypes(user.userTypes) }}</td>
                <td>{{ user.userGroup }}</td>
                <td>{{ user.createDate }}</td>
                <td>
                  <button @click="editUser(user)" class="action-button">
                    <i class="fas fa-edit"></i>
                  </button>
                  <button
                    @click="openResendModal(user)"
                    class="action-button"
                    title="Resend onboarding email"
                  >
                    <i class="fas fa-paper-plane"></i>
                  </button>
                  <button @click="requestDelete(user)" class="action-button">
                    <i class="fas fa-trash-alt"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          <div class="pagination">
            <button @click="prevPage" :disabled="currentPage === 1">
              Previous
            </button>
            <span>Page {{ currentPage }} of {{ totalPages }}</span>
            <button @click="nextPage" :disabled="currentPage === totalPages">
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useSessionStore } from "@/stores/session";
import { useThemeStore } from "@/stores/theme";
import adminApi from "@/services/manage_api";
import Papa from "papaparse";
import swal from "@/services/swal";
import AdminHelpButton from "@/components/AdminHelpButton.vue";
import { USER_MANAGEMENT_HELP } from "@/utils/admin_help";
export default {
  name: "UsersPage",
  components: { AdminHelpButton },
  setup() {
    const session = useSessionStore();

    const theme = useThemeStore();

    return {
      theme,
      session,
      USER_MANAGEMENT_HELP,
    };
  },
  data() {
    return {
      isEditing: false,
      isFormVisible: false,
      form: {
        userId: "",
        firstName: "",
        lastName: "",
        username: "",
        email: "",
        phone: "",
        password: "",
        confirmPassword: "",
        userTypes: [],
        userGroup: "",
        smsOptIn: false,
        emailOptIn: false,
        enableSessionManipulation: false,
        runSummarization: true,
        preSurvey: true,
        postSurvey: true,
        phoneVerified: false,
        phoneConfirmed: false,
        searchTimeout: null,
        disableUser: false,
      },
      // --- Bulk archive ---
      isBulkArchiveVisible: false,
      bulkArchiveMode: "archive",
      bulkArchiveFile: null,
      bulkArchiveEmails: [],
      bulkArchiveError: "",
      // The preview, kept so execute results can be diffed against what the
      // admin was shown. Cleared whenever the file or mode changes, which is
      // what re-disables the execute button.
      bulkArchivePlan: null,
      bulkArchiveResult: null,
      bulkArchiveBusy: false,
      // Archive state for the account currently open in the edit form, fetched
      // once when it opens. Null until then, and for the Add User form.
      archiveInfo: null,
      archiveInfoLoading: false,
      archiveListExpanded: false,
      archiveBusy: false,
      includeArchived: false,
      groups: [], // array of strings
      groupsLoading: false,
      groupsError: "",
      groupsFetched: false, // cache so we only fetch once per page open
      searchQuery: "",
      sortKey: "",
      users: [
        // Example users data
        // { userId: '1', email: 'user4@example.com', userTypes: ['Admin'], userGroup: 'civilian', createDate: '2021-01-01' },
        // { userId: '2', email: 'user2@example.com', userTypes: ['User'], userGroup: 'civilian', createDate: '2021-02-01' },
        // { userId: '3', email: 'user3@example.com', userTypes: ['User'], userGroup: 'civilian', createDate: '2021-03-01' },
        // { userId: '4', email: 'user6@example.com', userTypes: ['User'], userGroup: 'civilian', createDate: '2021-05-01' },
        // { userId: '5', email: 'user5@example.com', userTypes: ['Mentor'], userGroup: 'military', createDate: '2021-02-05' },
        // { userId: '6', email: 'user7@example.com', userTypes: ['Mentor'], userGroup: 'civilian', createDate: '2021-06-01' },
        // Add more users as needed
      ],
      currentPage: 1,
      pageSize: 10, // Default page size, can be set from API
      totalPages: 1,
      isBulkUploadVisible: false,
      selectedFile: null,
      csvPreview: [],
      csvHeaders: [],
      bulkUploadError: "",
      bulkUploadSuccess: "",
      isUploading: false,
      isUploadLocked: false,
      debug: false,
      isDeleteConfirmVisible: false,
      deleteTarget: { userId: null, email: "" },
      deleteConfirmText: "",
      isDeleting: false,
      deleteError: "",
      deleteSuccess: "",
      isResendModalVisible: false,
      resendTarget: { userId: null, email: "" },
      resendPassword: "",
      resendConfirmPassword: "",
      resendBusy: false,
      resendError: "",
      bulkSendNotifications: true, // default: ON
      sendOnboardingEmail: true,
      bulkUpdateMode: false,
    };
  },
  computed: {
    bulkArchiveModeLabel() {
      return this.bulkArchiveMode === "archive" ? "Archive" : "Unarchive";
    },
    bulkArchivePlanActionable() {
      return this.bulkArchivePlan?.summary?.actionable || 0;
    },
    /** Execute stays closed until a preview exists for the loaded file and mode. */
    canExecuteBulkArchive() {
      return (
        !!this.bulkArchivePlan &&
        !this.bulkArchiveResult &&
        this.bulkArchivePlanActionable > 0 &&
        !this.bulkArchiveBusy
      );
    },
    bulkArchiveTable() {
      return this.bulkArchiveResult || this.bulkArchivePlan;
    },
    /** Outcome counts worth showing, in a fixed order, zeroes dropped. */
    bulkArchiveSummaryChips() {
      const table = this.bulkArchiveTable;
      if (!table) return [];
      const labels = {
        actionable: this.bulkArchiveResult ? "changed" : "will change",
        not_found: "nothing to do",
        already_archived: "already archived",
        not_archived: "not archived",
        blocked: "blocked",
        duplicate: "duplicate",
        invalid: "invalid",
      };
      return Object.keys(labels)
        .filter((key) => (table.summary?.[key] || 0) > 0)
        .map((key) => ({
          key,
          label: labels[key],
          count: table.summary[key],
        }));
    },
    filteredUsers() {
      let users = this.users.filter((user) => {
        return (
          user.userId.includes(this.searchQuery) ||
          user.email.includes(this.searchQuery) ||
          user.userTypes.some((type) => type.includes(this.searchQuery)) ||
          user.userGroup.includes(this.searchQuery) ||
          user.createDate.includes(this.searchQuery)
        );
      });

      if (this.sortKey) {
        users = users.sort((a, b) => {
          if (a[this.sortKey] < b[this.sortKey]) return -1;
          if (a[this.sortKey] > b[this.sortKey]) return 1;
          return 0;
        });
      }
      return users;
    },
    paginatedUsers() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.filteredUsers.slice(start, end);
    },
    passwordsMatch() {
      return this.form.password === this.form.confirmPassword;
    },
    userTypeSelected() {
      return this.form.userTypes.length > 0;
    },
    userGroupSelected() {
      return this.form.userGroup !== "";
    },
    phoneValid() {
      return /^\+1\d{10}$/.test(this.form.phone.trim());
    },
    phoneRequiredInvalid() {
      return this.form.smsOptIn && !this.phoneValid;
    },
    canConfirmDelete() {
      // Require an exact match of the email to confirm
      return (
        this.deleteConfirmText !== "" &&
        this.deleteTarget.email !== "" &&
        this.deleteConfirmText === this.deleteTarget.email
      );
    },
    resendPasswordsMatch() {
      return (
        this.resendPassword !== "" &&
        this.resendPassword === this.resendConfirmPassword
      );
    },
    canConfirmResend() {
      return this.resendPasswordsMatch && this.resendTarget.email !== "";
    },
  },
  watch: {
    //filteredUsers(newFilteredUsers) {
    //  this.totalPages = Math.ceil(newFilteredUsers.length / this.pageSize);
    //},
  },
  methods: {
    buildUsersQuery(extra = {}) {
      const q = { ...extra };
      // include current search text
      if (this.searchQuery && this.searchQuery.trim() !== "") {
        q.email = this.searchQuery.trim();
      }
      // Archived accounts are hidden unless asked for, so they do not pad
      // every result with rows nobody is looking for.
      if (this.includeArchived) {
        q.include_archived = true;
      }
      return q;
    },
    onSearchInput() {
      // Clear existing timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      // Set new timeout for debounced search
      this.searchTimeout = setTimeout(() => {
        this.searchForUsers();
      }, 500); // Wait 500ms after user stops typing
    },
    // Bulk upload methods
    showBulkUploadForm() {
      this.isBulkUploadVisible = true;
      this.resetBulkUploadForm();
    },

    hideBulkUploadForm() {
      this.isBulkUploadVisible = false;
      this.resetBulkUploadForm();
    },

    resetBulkUploadForm() {
      this.selectedFile = null;
      this.csvPreview = [];
      this.csvHeaders = [];
      this.bulkUploadError = "";
      this.bulkUploadSuccess = "";
      this.isUploading = false;
      this.isUploadLocked = false;
      this.bulkSendNotifications = true; // reset default
      this.bulkUpdateMode = false;
      if (this.$refs.csvFileInput) {
        this.$refs.csvFileInput.value = "";
      }
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      if (!file) {
        this.selectedFile = null;
        this.csvPreview = [];
        this.csvHeaders = [];
        this.isUploadLocked = false;
        return;
      }

      if (!file.name.toLowerCase().endsWith(".csv")) {
        this.bulkUploadError = "Please select a CSV file.";
        this.isUploadLocked = false;
        return;
      }

      this.selectedFile = file;
      this.bulkUploadError = "";
      this.isUploadLocked = false;
      this.parseCSV(file);
    },

    // ===== Column name policies =====
    validRequiredColumns(isUpdate = this.bulkUpdateMode) {
      if (isUpdate) {
        return ["Email"];
      }
      return [
        "Group name",
        // "First name", // Make this optional
        // "Last name", // Make this optional
        "Email",
        "Password",
        "User types",
      ];
    },
    validOptionalColumns() {
      return [
        "First name",
        "Last name",
        "Phone number",
        "Receive SMS reminders",
        "Receive email reminders",
        "Enable session manipulation",
        "Automate summarization",
        "Pre-survey",
        "Post-survey",
        "Is active",
        "Phone confirmed",
      ];
    },
    validAllColumnsSet() {
      const all = [
        ...this.validRequiredColumns(false),
        ...this.validOptionalColumns(),
      ].map((c) => this.normalizeColumnName(c));
      return new Set(all);
    },

    // Tiny Levenshtein (for suggestions)
    levenshtein(a, b) {
      const m = a.length,
        n = b.length;
      const dp = Array.from({ length: m + 1 }, (_, i) =>
        Array.from({ length: n + 1 }, (_, j) =>
          i === 0 ? j : j === 0 ? i : 0,
        ),
      );
      for (let i = 1; i <= m; i++) {
        for (let j = 1; j <= n; j++) {
          dp[i][j] = Math.min(
            dp[i - 1][j] + 1,
            dp[i][j - 1] + 1,
            dp[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
          );
        }
      }
      return dp[m][n];
    },
    closestHeaderName(bad, allowedSet) {
      const allowed = [...allowedSet];
      let best = null,
        bestDist = Infinity;
      for (const cand of allowed) {
        const d = this.levenshtein(bad, cand);
        if (d < bestDist) {
          bestDist = d;
          best = cand;
        }
      }
      return { name: best, distance: bestDist };
    },

    validateHeaders(headers) {
      const normalizedHeaders = headers.map((h) => this.normalizeColumnName(h));
      const allowed = this.validAllColumnsSet();

      // Required missing?
      const requiredMissing = this.validRequiredColumns(
        this.bulkUpdateMode,
      ).filter((col) => {
        const norm = this.normalizeColumnName(col);
        return !normalizedHeaders.includes(norm);
      });

      // Unknown columns (anything not in allow-list)
      const unknown = [];
      normalizedHeaders.forEach((h, idx) => {
        if (!allowed.has(h)) {
          unknown.push({ idx, original: headers[idx], normalized: h });
        }
      });

      // Duplicates (normalized)
      const seen = new Map();
      const duplicates = [];
      normalizedHeaders.forEach((h) => {
        seen.set(h, (seen.get(h) || 0) + 1);
      });
      seen.forEach((count, key) => {
        if (count > 1) {
          // collect originals that mapped to this normalized key
          const originals = headers.filter(
            (h) => this.normalizeColumnName(h) === key,
          );
          duplicates.push({ normalized: key, originals, count });
        }
      });

      // Build suggestions for unknowns
      const suggestions = unknown.map((u) => {
        const { name, distance } = this.closestHeaderName(
          u.normalized,
          allowed,
        );
        // Only suggest if reasonably close (heuristic)
        return { ...u, suggestion: distance <= 5 ? name : null };
      });

      return { requiredMissing, unknown, duplicates, suggestions };
    },

    // Pull a readable message out of Axios/FastAPI responses
    extractBackendError(err) {
      // Axios error?
      const res = err?.response;
      if (!res) return err?.message || "Network error";

      const data = res.data;

      // Common FastAPI shapes
      // A) {"message": "Not an admin user"}
      if (data?.message) return data.message;

      // B) {"detail": "..."} or {"detail": [{loc:[...], msg:"...", type:"..."}]}
      if (data?.detail) {
        if (Array.isArray(data.detail)) {
          return data.detail
            .map((d) => {
              const loc = Array.isArray(d.loc) ? d.loc.join(" > ") : d.loc;
              return loc ? `${d.msg} (${loc})` : d.msg;
            })
            .join("\n");
        }
        if (typeof data.detail === "string") return data.detail;
      }

      // C) Bulk errors: { success:false, errors:[{index,email,message}, ...] }
      if (Array.isArray(data?.errors) && data.errors.length) {
        return data.errors
          .map((e) => {
            const head = (
              e.index !== undefined ? `Row ${e.index + 2}` : ""
            ).trim();
            const who = e.email ? ` ${e.email}` : "";
            const prefix = [head, who].filter(Boolean).join(" -");
            return prefix ? `${prefix}: ${e.message}` : e.message;
          })
          .join("\n");
      }

      // D) Fallback: stringify data or status text
      if (typeof data === "string") return data;
      try {
        return JSON.stringify(data);
      } catch (e) {
        console.error(e);
      }
      return res.statusText || "Request failed";
    },

    // Format any backend *success-but-with-errors* payload (bulk uploads)
    formatBulkErrors(response) {
      if (!response) return "";
      // Prefer explicit server-provided message
      let msg = response.message || "";
      if (Array.isArray(response.errors) && response.errors.length) {
        const rows = response.errors.map((e) => {
          const row = e.index !== undefined ? `Row ${e.index + 2}` : "Row ?";
          const who = e.email ? ` (${e.email})` : "";
          return `• ${row}${who}: ${e.reason}`;
        });
        msg += (msg ? "\n" : "") + rows.join("\n");
      }
      return msg;
    },

    parseCSV(file) {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          if (results.errors.length > 0) {
            this.bulkUploadError = `CSV parsing error: ${results.errors[0].message}`;
            return;
          }
          if (this.debug) {
            console.groupCollapsed("[CSV] Parsed results");
            console.log("Headers:", results.meta.fields);
            console.table(results.data.slice(0, 10)); // first 10 rows
            console.groupEnd();
          }
          this.csvHeaders = results.meta.fields || [];
          this.csvPreview = results.data || [];

          // Sanity check ALL headers
          const { requiredMissing, unknown, duplicates, suggestions } =
            this.validateHeaders(this.csvHeaders);

          if (requiredMissing.length > 0) {
            this.bulkUploadError = `Missing required columns: ${requiredMissing.join(
              ", ",
            )}`;
            this.csvPreview = [];
            return;
          }

          if (unknown.length > 0) {
            // Build a readable message with suggestions
            const lines = unknown.map((u) => {
              const sug = suggestions.find((s) => s.idx === u.idx)?.suggestion;
              const hint = sug ? ` → did you mean "${sug}"?` : "";
              return `• Unknown column "${u.original}"${hint}`;
            });
            this.bulkUploadError =
              `The CSV contains unknown columns:\n${lines.join("\n")}\n\n` +
              `Allowed columns are:\n` +
              [
                ...this.validRequiredColumns(),
                ...this.validOptionalColumns(),
              ].join(", ");
            this.csvPreview = [];
            return;
          }

          if (duplicates.length > 0) {
            const dups = duplicates
              .map(
                (d) =>
                  `• ${d.originals.join('", "')} (normalize to "${
                    d.normalized
                  }")`,
              )
              .join("\n");
            this.bulkUploadError = `Duplicate columns detected (case/space-insensitive):\n${dups}`;
            this.csvPreview = [];
            return;
          }

          this.bulkUploadError = "";
        },
        error: (error) => {
          this.bulkUploadError = `Error reading file: ${error.message}`;
        },
      });
    },

    normalizeColumnName(name) {
      return name.toLowerCase().trim().replace(/\s+/g, " ");
    },

    getColumnValue(row, columnName) {
      const normalizedTarget = this.normalizeColumnName(columnName);
      for (const [key, value] of Object.entries(row)) {
        if (this.normalizeColumnName(key) === normalizedTarget) {
          return value;
        }
      }
      return "";
    },

    convertCSVToJSON() {
      return this.csvPreview.map((row, idx) => {
        // Parse user types (semicolon delimited)
        const userTypesStr = this.getColumnValue(row, "User types");
        const userTypes = userTypesStr
          .split(";")
          .map((type) => type.trim())
          .filter((type) => type);

        // Parse boolean values
        const parseBool = (value) => {
          if (value === undefined || value === null) return false;
          const normalized = value.toString().toLowerCase().trim();
          if (normalized === "") return false;
          return ["true", "yes", "1"].includes(normalized);
        };
        // Parse boolean values with default TRUE when omitted/blank
        const parseBoolDefaultTrue = (value) => {
          if (value === undefined || value === null) return true; // omitted -> true
          const normalized = value.toString().toLowerCase().trim();
          if (normalized === "") return true; // blank -> true
          return ["true", "yes", "1"].includes(normalized);
        };
        // Tri-state parser for update mode:
        //  - returns true/false if explicitly set
        //  - returns undefined if blank/omitted -> do not include this field
        const parseBoolTriState = (value) => {
          if (value === undefined || value === null) return undefined;
          const normalized = value.toString().toLowerCase().trim();
          if (normalized === "") return undefined;

          if (["true", "yes", "1"].includes(normalized)) return true;
          if (["false", "no", "0"].includes(normalized)) return false;

          // For weird values, you could log in debug and treat as undefined
          if (this.debug) {
            console.warn("[CSV] Unrecognized boolean value:", value);
          }
          return undefined;
        };

        // Clean phone number
        const phoneNumber = this.getColumnValue(row, "Phone number");
        const cleanPhone = phoneNumber ? phoneNumber.trim() : null;

        // capture raw cell values for debugging
        const raw_send_email = this.getColumnValue(
          row,
          "Receive email reminders",
        );
        const raw_send_sms = this.getColumnValue(row, "Receive SMS reminders");
        const raw_session = this.getColumnValue(
          row,
          "Enable session manipulation",
        );
        const raw_run_summary = this.getColumnValue(
          row,
          "Automate summarization",
        );
        const raw_pre_survey = this.getColumnValue(row, "Pre-survey");
        const raw_post_survey = this.getColumnValue(row, "Post-survey");
        const raw_is_active = this.getColumnValue(row, "Is active");
        const raw_phone_confirmed = this.getColumnValue(row, "Phone confirmed");

        const email = this.getColumnValue(row, "Email").trim();
        const password = this.getColumnValue(row, "Password").trim();
        const firstName = this.getColumnValue(row, "First name").trim();
        const lastName = this.getColumnValue(row, "Last name").trim();
        const groupName = this.getColumnValue(row, "Group name").trim();

        // CREATE MODE (bulkUpdateMode = false)
        if (!this.bulkUpdateMode) {
          const payload = {
            email,
            password,
            first_name: firstName,
            last_name: lastName,
            is_active: parseBoolDefaultTrue(raw_is_active),
            phone_number: cleanPhone && cleanPhone !== "" ? cleanPhone : null,
            // Parsed above into raw_phone_confirmed but previously never made
            // it into this payload, so a CSV row asking for a pre-confirmed
            // account (e.g. bulk-provisioned military users) was silently
            // created with phone_confirmed=false regardless. Matches
            // send_sms/send_email/enable_session_manipulation: the backend's
            // create-mode default is also false (routers/auth.py).
            phone_confirmed: parseBool(raw_phone_confirmed),
            roles: userTypes,
            group_name: groupName,
            send_sms: parseBool(raw_send_sms),
            send_email: parseBool(raw_send_email),
            enable_session_manipulation: parseBool(raw_session),
            run_summary: parseBoolDefaultTrue(raw_run_summary),
            pre_survey: parseBoolDefaultTrue(raw_pre_survey),
            post_survey: parseBoolDefaultTrue(raw_post_survey),
          };

          if (this.debug) {
            console.groupCollapsed(`[MAP][CREATE] Row ${idx + 2} (CSV line)`);
            console.table({
              email: payload.email,
              password: payload.password,
              first_name: payload.first_name,
              last_name: payload.last_name,
              group_name: payload.group_name,
              roles: payload.roles && payload.roles.join(";"),
              phone_number: payload.phone_number,
              "raw.Receive email reminders": raw_send_email,
              "parsed.send_email": payload.send_email,
              "raw.Receive SMS reminders": raw_send_sms,
              "parsed.send_sms": payload.send_sms,
              "raw.Enable session manipulation": raw_session,
              "parsed.enable_session_manipulation":
                payload.enable_session_manipulation,
              "raw.Automate summarization": raw_run_summary,
              "parsed.run_summary": payload.run_summary,
              "raw.Pre-survey": raw_pre_survey,
              "parsed.pre_survey": payload.pre_survey,
              "raw.Post-survey": raw_post_survey,
              "parsed.post_survey": payload.post_survey,
              "raw.Is active": raw_is_active,
              "parsed.is_active": payload.is_active,
              "raw.Phone confirmed": raw_phone_confirmed,
              "parsed.phone_confirmed": payload.phone_confirmed,
            });
            console.groupEnd();
          }

          return payload;
        }

        // UPDATE MODE (bulkUpdateMode = true)
        // Always include email (match key); everything else is optional/patchy.
        const payloadUpdate = {
          email,
        };

        // Strings: only include if non-empty
        if (password) payloadUpdate.password = password;
        if (firstName) payloadUpdate.first_name = firstName;
        if (lastName) payloadUpdate.last_name = lastName;
        if (groupName) payloadUpdate.group_name = groupName;

        // Roles: only include if at least one user type present
        if (userTypes.length > 0) {
          payloadUpdate.roles = userTypes;
        }

        // Phone: only include if non-empty
        if (cleanPhone && cleanPhone !== "") {
          payloadUpdate.phone_number = cleanPhone;
        }

        // Booleans: only include if explicitly set in CSV
        const sendSms = parseBoolTriState(raw_send_sms);
        if (sendSms !== undefined) payloadUpdate.send_sms = sendSms;
        const sendEmail = parseBoolTriState(raw_send_email);
        if (sendEmail !== undefined) payloadUpdate.send_email = sendEmail;
        const sessionManip = parseBoolTriState(raw_session);
        if (sessionManip !== undefined) {
          payloadUpdate.enable_session_manipulation = sessionManip;
        }
        const runSummary = parseBoolTriState(raw_run_summary);
        if (runSummary !== undefined) payloadUpdate.run_summary = runSummary;
        const preSurvey = parseBoolTriState(raw_pre_survey);
        if (preSurvey !== undefined) payloadUpdate.pre_survey = preSurvey;
        const postSurvey = parseBoolTriState(raw_post_survey);
        if (postSurvey !== undefined) payloadUpdate.post_survey = postSurvey;
        const isActive = parseBoolTriState(raw_is_active);
        if (isActive !== undefined) payloadUpdate.is_active = isActive;
        const phoneConfirmed = parseBoolTriState(raw_phone_confirmed);
        if (phoneConfirmed !== undefined) payloadUpdate.phone_confirmed = phoneConfirmed;

        if (this.debug) {
          console.groupCollapsed(`[MAP][UPDATE] Row ${idx + 2} (CSV line)`);
          console.table(payloadUpdate);
          console.groupEnd();
        }

        return payloadUpdate;
      });
    },

    async processBulkUpload() {
      if (!this.selectedFile || this.csvPreview.length === 0) {
        this.bulkUploadError = "Please select a valid CSV file.";
        return;
      }

      this.isUploading = true;
      this.bulkUploadError = "";
      this.bulkUploadSuccess = "";

      try {
        const usersData = this.convertCSVToJSON();
        if (this.debug) {
          console.groupCollapsed("[API] Bulk payload preview");
          console.table(
            usersData.map((u) => ({
              email: u.email,
              send_email: u.send_email,
              send_sms: u.send_sms,
              run_summary: u.run_summary,
              pre_survey: u.pre_survey,
              post_survey: u.post_survey,
              roles: u.roles && u.roles.join(";"),
              group_name: u.group_name,
            })),
          );
          console.groupEnd();
          console.time("[API] POST /manage/add-users");
        }
        // Validate each user's data
        for (let i = 0; i < usersData.length; i++) {
          const user = usersData[i];

          // Email is ALWAYS required (match key)
          if (!user.email) {
            throw new Error(`Row ${i + 2}: Missing required field "Email".`);
          }
          // In create mode, enforce all required columns
          if (!this.bulkUpdateMode) {
            if (
              !user.password ||
              // !user.first_name || // Make this optional
              // !user.last_name || // Make this optional
              !user.group_name ||
              !user.roles ||
              user.roles.length === 0
            ) {
              // Use the same canonical list as header validation, but
              // drop "Email" here since it's already checked above.
              const requiredNonEmail = this.validRequiredColumns(false)
                .filter((name) => name !== "Email")
                .join(", ");

              throw new Error(
                `Row ${
                  i + 2
                }: Missing required fields. Required: ${requiredNonEmail}.`,
              );
            }
          }
        }

        console.log("Uploading users:", usersData);

        const response = await adminApi.addUsers(usersData, {
          send_notifications: this.bulkUpdateMode
            ? false
            : this.bulkSendNotifications,
          update_only: this.bulkUpdateMode,
        });

        if (this.debug) {
          console.timeEnd("[API] POST /manage/add-users");
          console.groupCollapsed("[API] Bulk response");
          console.log(response);
          if (response?.errors?.length) console.table(response.errors);
          console.groupEnd();
        }

        if (response?.success) {
          const created = response?.created_count ?? 0;
          const updated = response?.updated_count ?? 0;

          if (this.bulkUpdateMode) {
            const effectiveUpdated = updated || created; // fallback to created_count if backend not yet updated
            this.bulkUploadSuccess = `Successfully updated ${effectiveUpdated} users.`;
          } else {
            this.bulkUploadSuccess = `Successfully uploaded ${
              created || usersData.length
            } users.`;
          }

          // Append any backend human message (e.g., onboarding emails queued)
          if (
            response?.message &&
            !this.bulkUploadSuccess.includes(response.message)
          ) {
            this.bulkUploadSuccess += `\n${response.message}`;
          }

          if ((created || updated) > 0) {
            this.isUploadLocked = true;
          }
          await this.fetchUsersData({});
        } else {
          const created = response?.created_count ?? 0;
          const updated = response?.updated_count ?? 0;
          const failed = response?.failed_count ?? 0;

          if ((created || updated) > 0) {
            const affected = this.bulkUpdateMode ? updated || created : created;
            this.bulkUploadSuccess =
              (this.bulkUpdateMode
                ? `Updated ${affected} users with ${failed} failures.\n`
                : `Uploaded ${affected} users with ${failed} failures.\n`) +
              (this.formatBulkErrors(response) || "");

            this.isUploadLocked = true;

            if (response?.message) {
              this.bulkUploadSuccess += `\n${response.message}`;
            }

            await this.fetchUsersData({});
          } else {
            this.bulkUploadError =
              this.formatBulkErrors(response) ||
              response?.message ||
              (this.bulkUpdateMode
                ? "Failed to update users."
                : "Failed to upload users.");
          }
        }
      } catch (error) {
        if (this.debug) {
          console.error("[API] Bulk upload error", error);
          if (error?.response) {
            console.error("Status:", error.response.status);
            console.error("Data:", error.response.data);
          }
        }
        console.error("Error uploading users:", error);
        this.bulkUploadError = this.extractBackendError(error);
      } finally {
        this.isUploading = false;
      }
    },

    requestDelete(user) {
      this.deleteTarget = { userId: user.userId, email: user.email };
      this.deleteConfirmText = "";
      this.deleteError = "";
      this.deleteSuccess = "";
      this.isDeleteConfirmVisible = true;
    },

    cancelDelete() {
      this.isDeleteConfirmVisible = false;
      this.isDeleting = false;
      this.deleteConfirmText = "";
      this.deleteError = "";
      this.deleteSuccess = "";
      this.deleteTarget = { userId: null, email: "" };
    },

    async confirmDelete() {
      if (!this.canConfirmDelete || this.isDeleting) return;
      this.isDeleting = true;
      this.deleteError = "";
      this.deleteSuccess = "";

      try {
        // Call your existing deletion logic
        const userDelete = {
          user_id: parseInt(this.deleteTarget.userId),
          email: this.deleteTarget.email,
        };
        const deleteUserMessage = await adminApi.deleteUser(userDelete);

        if (deleteUserMessage.message === "User successfully deleted") {
          // Update UI list
          this.users = this.users.filter(
            (u) => u.userId !== this.deleteTarget.userId,
          );
          this.deleteSuccess = `Email: ${this.deleteTarget.email} is deleted!`;

          // Brief success then close modal
          setTimeout(() => {
            this.cancelDelete();
          }, 1200);
        } else if (deleteUserMessage.message === "No such user") {
          this.deleteError = `Email: ${this.deleteTarget.email} is not found!`;
        } else if (deleteUserMessage.message === "Not an admin user") {
          this.deleteError = "You are not an admin user!";
        } else if (deleteUserMessage.message === "Something went wrong") {
          this.deleteError = "Something went wrong!";
        } else {
          this.deleteError =
            deleteUserMessage.message || "Something went wrong!";
        }
      } catch (err) {
        // Reuse your error extractor if you like
        this.deleteError = this.extractBackendError
          ? this.extractBackendError(err)
          : err?.message || "Failed to delete user.";
      } finally {
        this.isDeleting = false;
      }
    },

    openResendModal(user) {
      this.resendTarget = { userId: user.userId, email: user.email };
      this.resendPassword = "";
      this.resendConfirmPassword = "";
      this.resendError = "";
      this.isResendModalVisible = true;
    },

    cancelResend() {
      this.isResendModalVisible = false;
      this.resendBusy = false;
      this.resendPassword = "";
      this.resendConfirmPassword = "";
      this.resendError = "";
      this.resendTarget = { userId: null, email: "" };
    },

    async confirmResend() {
      if (!this.canConfirmResend || this.resendBusy) return;

      const email = this.resendTarget.email;
      const confirmed = await swal.fire({
        icon: "warning",
        title: "Reset password & resend?",
        html:
          `<p style="text-align:left">This immediately changes the login password for ` +
          `<b>${email}</b> and re-sends the onboarding email with the new password. ` +
          `Any devices currently signed in will be signed out.</p>`,
        showCancelButton: true,
        confirmButtonText: "Reset & Resend",
      });
      if (!confirmed.isConfirmed) return;

      this.resendBusy = true;
      this.resendError = "";
      try {
        const result = await adminApi.resendOnboardingEmail(
          email,
          this.resendPassword,
        );
        if (!result.success) {
          this.resendError = result.message || "Something went wrong!";
          return;
        }
        swal.fire({
          icon: "success",
          title: "Onboarding email re-sent",
          text: `A new password was set for ${email} and the onboarding email was re-sent.`,
        });
        this.cancelResend();
      } catch (error) {
        console.error("Resend failed:", error);
        this.resendError = this.extractBackendError
          ? this.extractBackendError(error)
          : error?.message || "Failed to resend onboarding email.";
      } finally {
        this.resendBusy = false;
      }
    },

    // group fetching helpers so backend can return either strings or objects
    gKey(g) {
      return typeof g === "string"
        ? g
        : g.id ?? g.name ?? g.slug ?? JSON.stringify(g);
    },
    gValue(g) {
      return typeof g === "string" ? g : g.name ?? g.slug ?? "";
    },
    gLabel(g) {
      return typeof g === "string"
        ? g
        : g.display_name ?? g.name ?? g.slug ?? "";
    },

    async fetchGroupsIfNeeded() {
      if (this.groupsFetched || this.groupsLoading) return;
      this.groupsLoading = true;
      this.groupsError = "";
      try {
        const res = await adminApi.getGroups();
        // Accept either { groups: [...] } or [...] for flexibility
        const arr = Array.isArray(res) ? res : res?.groups ?? [];
        this.groups = arr;

        // If editing a user whose group isn't returned, keep it visible
        if (this.isEditing && this.form.userGroup) {
          const hasCurrent = arr.some(
            (g) => this.gValue(g) === this.form.userGroup,
          );
          if (!hasCurrent)
            this.groups = [
              { name: this.form.userGroup, display_name: this.form.userGroup },
              ...arr,
            ];
        }
        this.groupsFetched = true;
      } catch (e) {
        console.error("Failed to load groups", e);
        this.groupsError = "Failed to load groups";
      } finally {
        this.groupsLoading = false;
      }
    },
    formatUserTypes(userTypes) {
      return userTypes.join("; ");
    },
    async searchForUsers() {
      // Check if searchQuery is not null/empty
      if (!this.searchQuery || this.searchQuery.trim() === "") {
        console.log("Search query is empty");
        await this.fetchUsersData(this.buildUsersQuery({ page: 1 }));
        return;
      }
      try {
        this.isLoading = true; // Show loading indicator if you have one
        // Call the getUsers API method
        const response = await adminApi.getUsers(
          this.buildUsersQuery({ page: 1 }),
        );
        // Update the users list with search results
        if (response && response.users) {
          this.users = response.users;
          this.totalPages = response.total_page_num;
          this.currentPage = 1; // Reset to first page
          // console.log("Search results:", response.users);
        } else {
          this.users = [];
          this.totalPages = 0;
          this.currentPage = 1;
          console.log("No users found");
        }
      } catch (error) {
        console.error("Error searching for users:", error);
        // Handle error - maybe show an error message
        this.users = [];
        this.totalPages = 0;
        this.currentPage = 1;
      } finally {
        this.isLoading = false; // Hide loading indicator
      }
    },
    async clearSearch() {
      this.searchQuery = "";
      await this.fetchUsersData({}); // Fetch all users
    },
    showForm() {
      this.isFormVisible = true;
      this.isEditing = false;
      this.resetForm();
      this.fetchGroupsIfNeeded();
    },
    hideForm() {
      this.isFormVisible = false;
    },
    resetForm() {
      this.form = {
        userId: "",
        firstName: "",
        lastName: "",
        username: "",
        email: "",
        phone: "",
        password: "",
        confirmPassword: "",
        userTypes: [],
        userGroup: "",
        smsOptIn: false,
        emailOptIn: false,
        enableSessionManipulation: false,
        runSummarization: true,
        preSurvey: true,
        postSurvey: true,
        phoneVerified: false,
        phoneConfirmed: false,
        disableUser: false,
        sendOnboardingEmail: true,
      };
    },
    goBack() {
      this.$router.go(-1);
    },
    async submitForm() {
      if (
        this.passwordsMatch &&
        this.userTypeSelected &&
        this.userGroupSelected
      ) {
        if (this.isEditing) {
          await this.updateUser();
        } else {
          await this.addUser();
        }
        this.hideForm();
      }
    },
    sortBy(key) {
      this.sortKey = key;
    },
    async addUser() {
      // Handle add user logic here
      console.log("User added:", this.form);
      try {
        const userCreate = {
          email: this.form.email,
          password: this.form.password,
          first_name: this.form.firstName,
          last_name: this.form.lastName,
          is_active: !this.form.disableUser,
          phone_number:
            this.form.phone && this.form.phone.trim() !== ""
              ? this.form.phone.trim()
              : null,
          roles: this.form.userTypes,
          group_name: this.form.userGroup,
          send_sms: this.form.smsOptIn,
          send_email: this.form.emailOptIn,
          enable_session_manipulation: this.form.enableSessionManipulation,
          run_summary: this.form.runSummarization,
          pre_survey: this.form.preSurvey,
          post_survey: this.form.postSurvey,
          send_onboarding_email: this.form.sendOnboardingEmail,
        };

        const addUserMessage = await adminApi.addUser(userCreate);
        if (addUserMessage.message === "User successfully signed up") {
          const newUser = {
            userId: addUserMessage.id.toString(),
            firstName: this.form.firstName,
            lastName: this.form.lastName,
            email: this.form.email,
            phone: this.form.phone,
            userTypes: this.form.userTypes,
            userGroup: this.form.userGroup,
            createDate: addUserMessage.create_date,
            smsOptIn: this.form.smsOptIn,
            emailOptIn: this.form.emailOptIn,
            enableSessionManipulation: this.form.enableSessionManipulation,
            runSummarization: this.form.runSummarization,
            preSurvey: this.form.preSurvey,
            postSurvey: this.form.postSurvey,
            isActive: !this.form.disableUser,
          };
          this.users.push({ ...newUser });
        } else if (addUserMessage.message === "Email already registered") {
          // TODO: Alert popup is needed
          swal.fire({
            icon: "error",
            text: `Email: ${this.form.email} is already registered!`,
          });
        } else if (addUserMessage.message === "Not an admin user") {
          // TODO: Alert popup is needed
          swal.fire({ icon: "error", text: "You are not an admin user!" });
        } else if (addUserMessage.message === "Need password") {
          // TODO: Alert popup is needed
          swal.fire({ icon: "error", text: "Password is needed!" });
        } else {
          // TODO: Alert popup is needed
          swal.fire({ icon: "error", text: "Something went wrong!" });
        }
      } catch (error) {
        console.error("Error adding user:", error);
      }
    },
    async updateUser() {
      try {
        console.log("User updated:", this.form);
        const userUpdate = {
          email: this.form.email,
          password: this.form.password,
          first_name: this.form.firstName,
          last_name: this.form.lastName,
          is_active: !this.form.disableUser,
          phone_number:
            this.form.phone && this.form.phone.trim() !== ""
              ? this.form.phone.trim()
              : null,
          roles: this.form.userTypes,
          group_name: this.form.userGroup,
          send_sms: this.form.smsOptIn,
          send_email: this.form.emailOptIn,
          enable_session_manipulation: this.form.enableSessionManipulation,
          run_summary: this.form.runSummarization,
          pre_survey: this.form.preSurvey,
          post_survey: this.form.postSurvey,
          phone_confirmed: this.form.phoneConfirmed,
        };

        const updateUserMessage = await adminApi.editUser(userUpdate);
        if (updateUserMessage.message === "User successfully updated") {
          const updatedUser = {
            userId: this.form.userId,
            email: this.form.email,
            phone: this.form.phone,
            firstName: this.form.firstName,
            lastName: this.form.lastName,
            userTypes: this.form.userTypes,
            userGroup: this.form.userGroup,
            smsOptIn: this.form.smsOptIn,
            emailOptIn: this.form.emailOptIn,
            enableSessionManipulation: this.form.enableSessionManipulation,
            runSummarization: this.form.runSummarization,
            preSurvey: this.form.preSurvey,
            postSurvey: this.form.postSurvey,
            isActive: !this.form.disableUser,
          };
          const index = this.users.findIndex(
            (user) => user.userId === this.form.userId,
          );
          if (index !== -1) {
            this.users.splice(index, 1, {
              ...updatedUser,
              createDate: this.users[index].createDate,
            });
          }
          swal.fire({
            icon: "success",
            text: `Email: ${this.form.email} has been updated!`,
          });
        } else if (
          updateUserMessage.message === "Cannot find the target user"
        ) {
          // TODO: Alert popup is needed
          swal.fire({
            icon: "error",
            text: `Email: ${this.form.email} is not found!`,
          });
        } else if (updateUserMessage.message === "Not an admin user") {
          // TODO: Alert popup is needed
          swal.fire({ icon: "error", text: "You are not an admin user!" });
        } else {
          // TODO: Alert popup is needed
          swal.fire({ icon: "error", text: "Something went wrong!" });
        }
      } catch (error) {
        console.error("Error updating user:", error);
      }
    },
    editUser(user) {
      this.isFormVisible = true;
      this.isEditing = true;
      //this.form = { ...user, password: '', confirmPassword: '', userTypes: user.userTypes || [], userGroup: user.userGroup || '' };
      this.fetchUserData(user);
      this.loadArchiveInfo(user.email);
    },
    showBulkArchiveForm() {
      this.isBulkArchiveVisible = true;
      this.resetBulkArchive();
    },
    hideBulkArchiveForm() {
      this.isBulkArchiveVisible = false;
      this.resetBulkArchive();
    },
    resetBulkArchive() {
      this.bulkArchiveMode = "archive";
      this.bulkArchiveFile = null;
      this.bulkArchiveEmails = [];
      this.bulkArchiveError = "";
      this.bulkArchivePlan = null;
      this.bulkArchiveResult = null;
    },
    /** Any change to the input invalidates the preview, which re-disables execute. */
    invalidateBulkArchivePlan() {
      this.bulkArchivePlan = null;
      this.bulkArchiveResult = null;
    },
    handleBulkArchiveFile(event) {
      const file = event.target.files?.[0];
      this.bulkArchiveError = "";
      this.invalidateBulkArchivePlan();
      if (!file) {
        this.bulkArchiveFile = null;
        this.bulkArchiveEmails = [];
        return;
      }
      this.bulkArchiveFile = file;
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          if (results.errors.length > 0) {
            this.bulkArchiveError = `CSV parsing error: ${results.errors[0].message}`;
            this.bulkArchiveEmails = [];
            return;
          }
          // Only Email is read, and unknown columns are ignored on purpose:
          // this is what lets the untouched roster file be fed straight in.
          const rows = results.data || [];
          const emails = rows
            .map((row) => (this.getColumnValue(row, "Email") || "").trim())
            .filter((e) => e !== "");
          if (emails.length === 0) {
            this.bulkArchiveError =
              'No addresses found. The file needs an "Email" column; every other column is ignored.';
            this.bulkArchiveEmails = [];
            return;
          }
          this.bulkArchiveEmails = emails;
        },
        error: (error) => {
          this.bulkArchiveError = `Error reading file: ${error.message}`;
          this.bulkArchiveEmails = [];
        },
      });
    },
    async runBulkArchive(dryRun) {
      if (this.bulkArchiveEmails.length === 0) return;
      if (!dryRun) {
        const confirmed = await swal.fire({
          icon: "warning",
          title: `${this.bulkArchiveModeLabel} ${this.bulkArchivePlanActionable} accounts?`,
          html:
            `<p style="text-align:left">This writes the changes previewed below.</p>` +
            `<p style="text-align:left">Addresses are recomputed as they are written, so a ` +
            `result may differ from the preview if the database changed since.</p>`,
          showCancelButton: true,
          confirmButtonText: this.bulkArchiveModeLabel,
        });
        if (!confirmed.isConfirmed) return;
      }

      this.bulkArchiveBusy = true;
      this.bulkArchiveError = "";
      try {
        const result = await adminApi.bulkArchive({
          emails: this.bulkArchiveEmails,
          mode: this.bulkArchiveMode,
          dryRun,
        });
        if (dryRun) {
          this.bulkArchivePlan = result;
          this.bulkArchiveResult = null;
        } else {
          this.bulkArchiveResult = result;
          await this.refreshUserList();
        }
      } catch (error) {
        console.error("Bulk archive failed:", error);
        this.bulkArchiveError = "Something went wrong running the batch.";
      } finally {
        this.bulkArchiveBusy = false;
      }
    },
    /** True when execute produced a different address than the preview promised. */
    bulkArchiveRowDrifted(row) {
      if (!this.bulkArchiveResult || !this.bulkArchivePlan) return false;
      const planned = this.bulkArchivePlan.rows.find((r) => r.index === row.index);
      return (
        !!planned?.result_email &&
        !!row.result_email &&
        planned.result_email !== row.result_email
      );
    },
    bulkArchivePlannedEmail(row) {
      return this.bulkArchivePlan?.rows.find((r) => r.index === row.index)
        ?.result_email;
    },
    exportBulkArchiveResults() {
      const table = this.bulkArchiveResult || this.bulkArchivePlan;
      if (!table) return;
      const phase = this.bulkArchiveResult ? "result" : "plan";
      const header = [
        "index",
        "email",
        "outcome",
        "result_email",
        "user_id",
        "applied",
        "detail",
      ];
      const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      const csv = [
        header.join(","),
        ...table.rows.map((r) => header.map((h) => escape(r[h])).join(",")),
      ].join("\n");

      const stamp = new Date().toISOString().slice(0, 10);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${table.mode}-${phase}-${stamp}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    },
    async loadArchiveInfo(email) {
      this.archiveInfo = null;
      this.archiveListExpanded = false;
      if (!email || !this.session.rolesIncludes("admin")) return;
      this.archiveInfoLoading = true;
      try {
        this.archiveInfo = await adminApi.getArchiveInfo(email);
      } catch (error) {
        // A failed lookup hides the archive controls rather than blocking the
        // rest of the form, which has nothing to do with archiving.
        console.error("Could not load archive info:", error);
        this.archiveInfo = null;
      } finally {
        this.archiveInfoLoading = false;
      }
    },
    async archiveUser() {
      const email = this.form.email;
      const confirmed = await swal.fire({
        icon: "warning",
        title: "Archive this account?",
        html:
          `<p style="text-align:left">The account keeps its sessions and summaries, ` +
          `is moved to an archive address, and is deactivated.</p>` +
          `<p style="text-align:left"><b>${email}</b> then becomes available for a new account.</p>` +
          `<p style="text-align:left">No new account is created by this step.</p>`,
        showCancelButton: true,
        confirmButtonText: "Archive",
      });
      if (!confirmed.isConfirmed) return;

      this.archiveBusy = true;
      try {
        const result = await adminApi.archiveUser(email);
        if (!result.success) {
          swal.fire({ icon: "error", text: result.message });
          return;
        }
        swal.fire({
          icon: "success",
          title: "Account archived",
          html:
            `<p style="text-align:left">Moved to <b>${result.email}</b>.</p>` +
            `<p style="text-align:left"><b>${result.freed_email}</b> is now available.</p>`,
        });
        this.hideForm();
        await this.refreshUserList();
      } catch (error) {
        console.error("Archive failed:", error);
        swal.fire({ icon: "error", text: "Something went wrong!" });
      } finally {
        this.archiveBusy = false;
      }
    },
    async unarchiveUser() {
      const email = this.form.email;
      const original = this.archiveInfo?.original_email;
      const confirmed = await swal.fire({
        icon: "warning",
        title: "Unarchive this account?",
        html:
          `<p style="text-align:left">The account returns to <b>${original}</b> ` +
          `and becomes active again.</p>`,
        showCancelButton: true,
        confirmButtonText: "Unarchive",
      });
      if (!confirmed.isConfirmed) return;

      this.archiveBusy = true;
      try {
        const result = await adminApi.unarchiveUser(email);
        if (!result.success) {
          swal.fire({ icon: "error", text: result.message });
          return;
        }
        swal.fire({
          icon: "success",
          text: `Account restored to ${result.email}.`,
        });
        this.hideForm();
        await this.refreshUserList();
      } catch (error) {
        console.error("Unarchive failed:", error);
        swal.fire({ icon: "error", text: "Something went wrong!" });
      } finally {
        this.archiveBusy = false;
      }
    },
    async refreshUserList() {
      // Archiving changes both an address and an archived state, so the cached
      // row cannot be patched in place the way an ordinary edit is.
      await this.fetchUsersData(this.buildUsersQuery({ page: this.currentPage }));
    },
    async toggleIncludeArchived() {
      this.currentPage = 1;
      await this.fetchUsersData(this.buildUsersQuery({ page: 1 }));
    },
    fetchUserData(user) {
      // Simulate an API call to fetch user data
      console.log(
        "Fetching user data for:",
        user.userId,
        user.email,
        user.userTypes,
        user.userGroup,
        user.smsOptIn,
        user.emailOptIn,
        user.enableSessionManipulation,
        user.runSummarization,
        user.preSurvey,
        user.postSurvey,
        user.phoneVerified,
        user.phoneConfirmed,
      );
      if (user) {
        // Dummy data corresponding to the user
        // const dummyData = {
        //   userId: userId,
        //   firstName: 'John',
        //   lastName: 'Doe',
        //   username: 'johndoe',
        //   email: email,
        //   phone: '123-456-7890',
        //   userTypes: ['user'],
        //   userGroup: 'civilian'
        // };
        const userData = {
          userId: user.userId,
          firstName: user.firstName,
          lastName: user.lastName,
          username: "",
          email: user.email,
          phone: user.phone,
          userTypes: user.userTypes,
          userGroup: user.userGroup,
          smsOptIn: user.smsOptIn,
          emailOptIn: user.emailOptIn,
          enableSessionManipulation: user.enableSessionManipulation,
          runSummarization: user.runSummarization,
          preSurvey: user.preSurvey,
          postSurvey: user.postSurvey,
          phoneVerified: user.phoneVerified,
          phoneConfirmed: user.phoneConfirmed,
          isActive: user.isActive !== undefined ? user.isActive : true,
          sendOnboardingEmail: false,
        };
        // Populate the form with the fetched data
        // this.form = { ...dummyData, password: '', confirmPassword: '' };
        this.form = {
          ...userData,
          password: "",
          confirmPassword: "",
          disableUser: userData.isActive === false,
        };
      }
    },
    async fetchUsersData(query) {
      console.log("Fetching users data");
      const usersData = await adminApi.getUsers(query);
      this.users = usersData.users;
      this.totalPages = usersData.total_page_num;
    },
    async deleteUser(userId, email) {
      // Handle delete user logic here
      // The button for deleting a user should be converted to a asynchronous one
      console.log("Delete user:", parseInt(userId));

      const userDelete = {
        user_id: parseInt(userId),
        email: email,
      };

      const deleteUserMessage = await adminApi.deleteUser(userDelete);
      if (deleteUserMessage.message === "User successfully deleted") {
        this.users = this.users.filter((user) => user.userId !== userId);
        swal.fire({ icon: "success", text: `Email: ${email} is deleted!` });
      } else if (deleteUserMessage.message === "No such user") {
        // TODO: Alert popup is needed
        swal.fire({ icon: "error", text: `Email: ${email} is not found!` });
      } else if (deleteUserMessage.message === "Not an admin user") {
        // TODO: Alert popup is needed
        swal.fire({ icon: "error", text: "You are not an admin user!" });
      } else if (deleteUserMessage.message === "Something went wrong") {
        // TODO: Alert popup is needed
        swal.fire({ icon: "error", text: "Something went wrong!" });
      } else {
        // TODO: Alert popup is needed
        swal.fire({ icon: "error", text: "Something went wrong!" });
      }
    },
    async getMentees(mentorId) {
      try {
        const mentees = await adminApi.getMentees(mentorId);
        this.users = mentees;
        this.totalPages = Math.ceil(this.users.length / this.pageSize);
      } catch (error) {
        console.error("Error getting messssntees:", error);
      }
    },
    async nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        await this.fetchUsersData(
          this.buildUsersQuery({ page: this.currentPage }),
        );
      }
    },
    async prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        await this.fetchUsersData(
          this.buildUsersQuery({ page: this.currentPage }),
        );
      }
    },
  },
  async mounted() {
    // Call getMentees with a dummy ID
    await this.fetchUsersData(this.buildUsersQuery({ page: 1 }));
  },
};
</script>

<style scoped>
.error-message {
  color: red;
  text-align: center; /* Center the error message */
  margin-bottom: 10px;
  font-size: large;
  white-space: pre-wrap;
}

.new-main-page {
  display: flex;
  flex-direction: column;
  height: var(--app-height);
  min-height: 100vh;
  min-height: -webkit-fill-available;
}

.new-main-page h1,
.new-main-page p,
.new-main-page form,
.new-main-page div {
  width: 100%;
  text-align: left;
}

.new-main-header {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 150px; /* Adjust as needed */
  padding: 0;
  margin: 0;
}

.new-main-page-header-text-old {
  height: 40px;
  margin-top: 2%;
  margin-left: 20%;
}

.new-main-page-header-text {
  height: 40px;
  margin-top: 2%;
  margin-left: 20%;
  font-size: 1.5em;
  font-family: "Roboto", sans-serif;
}

.new-main-page-header-text {
  font-size: 1.5em;
  margin-top: 2%;
  margin-left: 0%;
  font-family: "Roboto", sans-serif;
}
.content {
  padding: 10px;
  background-color: #ffffffb2;
  border-radius: 10px; /* Rounded corners */
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1); /* Optional: Add a shadow for better visibility */
  margin: 20px; /* Optional: Add margin for spacing */
  width: calc(100% - 40px); /* Full width minus margins */
  height: 100%;
  min-height: auto;
  overflow-y: auto; /* Enable vertical scrolling */
}

.form-group {
  margin-bottom: 15px;
  flex-direction: column;
  align-items: center;
}

.form-title {
  /* The `font` shorthand resets size, weight and line-height, so the separate
     font-weight: bold and font-size: 30px this rule used to carry never took
     effect. Declared once here so what is written is what renders. */
  font: 900 1.25em/1.3 "Roboto", sans-serif;
  /* Was margin-top: 2%. A percentage vertical margin resolves against the
     container's *width*, so the gap above the title grew as the window widened
     and pushed the table into scrolling on exactly the large screens that had
     room to spare. */
  margin-top: 4px;
  margin-bottom: 6px;
  width: 100%; /* Ensure the title takes the full width */
  text-align: left; /* Align text to the left */
}

.page-title-row {
  align-items: center;
  display: grid;
  grid-template-columns: minmax(0, 1fr) max-content;
  gap: 12px;
  margin-bottom: 10px;
  width: 100%;
}

.page-title-row .form-title {
  flex: 0 1 auto;
  margin-bottom: 0;
  width: auto;
}

.page-title-row .page-help-button {
  justify-self: end;
  width: auto;
}

.page-title-row :deep(.admin-help),
.page-title-row :deep(.admin-help-trigger) {
  justify-content: flex-end;
  width: auto;
}

label {
  font: 1.5em "Inter", sans-serif;
  width: 30%;
  margin-bottom: 5px;
}

input {
  padding: 10px;
  font-size: 16px;
  border: 2px solid #ccc;
  border-radius: 4px;
  flex: 1;
}

.full-width {
  width: 70%;
  margin-left: 30%;
}

.show-form-button {
  margin-top: 20px;
  margin-right: 20px;
  padding: 10px 20px;
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 10px;
  cursor: pointer;
}

.show-form-button:hover {
  background-color: var(--primary-color);
}

/* Archive
   Amber marks the archived state throughout: the badge in the table, the card
   in the edit form, and the blocked-unarchive callout. Live accounts stay
   neutral so the section does not shout on every user you open. */

.archive-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
  margin-right: 20px;
  /* .actions sets justify-content but never display:flex, so these controls are
     inline boxes on a text line. Matching the buttons' vertical padding keeps
     the pill the same height as its neighbours, and middle alignment stops the
     shorter box from hanging off their baseline. */
  padding: 10px 18px 10px 12px;
  vertical-align: middle;
  box-sizing: border-box;
  border: 1px solid #d7dbe0;
  border-radius: 20px;
  background-color: #fff;
  font-size: 0.85rem;
  color: #5c6470;
  white-space: nowrap;
  cursor: pointer;
  transition:
    border-color 0.2s,
    background-color 0.2s,
    color 0.2s;
}

.archive-toggle:hover {
  border-color: #b9c0c8;
}

.archive-toggle--on {
  border-color: #e0b155;
  background-color: #fdf6e7;
  color: #8a5a10;
}

.archive-toggle-text {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.archive-toggle-text i {
  font-size: 0.8rem;
  opacity: 0.75;
}

/* Same switch the chat page uses, one size down to sit in the actions row. */
.switch--sm {
  position: relative;
  display: inline-block;
  width: 34px;
  height: 19px;
  flex-shrink: 0;
}

.switch--sm input {
  opacity: 0;
  width: 0;
  height: 0;
}

.switch--sm .slider {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  border-radius: 19px;
  transition: 0.3s;
  cursor: pointer;
}

.switch--sm .slider:before {
  position: absolute;
  content: "";
  height: 13px;
  width: 13px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  border-radius: 50%;
  transition: 0.3s;
}

.switch--sm input:checked + .slider {
  background-color: #d69e2e;
}

.switch--sm input:checked + .slider:before {
  transform: translateX(15px);
}

.switch--sm input:focus-visible + .slider {
  box-shadow: 0 0 0 2px rgba(214, 158, 46, 0.4);
}

.archived-badge {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 9px;
  border: 1px solid #edd9a3;
  border-radius: 20px;
  background-color: #fdf6e7;
  color: #8a5a10;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  vertical-align: middle;
}

.archive-section {
  margin-top: 10px;
}

.archive-card {
  border: 1px solid #e3e6ea;
  border-radius: 10px;
  background-color: #fafbfc;
  overflow: hidden;
}

.archive-card--archived {
  border-color: #edd9a3;
  background-color: #fffdf7;
}

.archive-card-body {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
}

.archive-icon {
  flex-shrink: 0;
  margin-top: 2px;
  font-size: 1.05rem;
  color: #9aa2ac;
}

.archive-card--archived .archive-icon {
  color: #d69e2e;
}

.archive-copy {
  flex: 1;
  min-width: 0;
}

.archive-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 4px;
  font-size: 0.92rem;
  font-weight: 600;
  color: #2f3640;
}

.archive-title .archived-badge {
  margin-left: 0;
}

.archive-date {
  font-size: 0.8rem;
  font-weight: 400;
  color: #7a828c;
}

.archive-desc {
  margin: 0;
  font-size: 0.83rem;
  line-height: 1.5;
  color: #5c6470;
  overflow-wrap: anywhere;
}

.archive-blocked {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 10px 0 0;
  padding: 9px 11px;
  border-radius: 8px;
  background-color: #fdf1e3;
  font-size: 0.8rem;
  line-height: 1.5;
  color: #8a4b10;
  overflow-wrap: anywhere;
}

.archive-blocked i {
  margin-top: 2px;
  flex-shrink: 0;
}

.archive-button {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  border: 1px solid #c2c8d0;
  border-radius: 10px;
  background-color: #fff;
  color: #4a525c;
  font-size: 0.85rem;
  cursor: pointer;
  transition:
    background-color 0.2s,
    border-color 0.2s,
    color 0.2s;
}

.archive-button:hover:not(:disabled) {
  border-color: #d69e2e;
  background-color: #d69e2e;
  color: #fff;
}

.archive-button--restore:hover:not(:disabled) {
  border-color: var(--primary-color);
  background-color: var(--primary-color);
  color: #fff;
}

.archive-button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.archive-history {
  border-top: 1px solid #e9ecef;
  padding: 0 16px;
}

.archive-card--archived .archive-history {
  border-top-color: #f0e4c6;
}

.archive-disclosure {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 0;
  border: none;
  background: none;
  font-size: 0.8rem;
  color: #5c6470;
  text-align: left;
  cursor: pointer;
}

.archive-disclosure:hover {
  color: var(--primary-color);
}

.archive-disclosure i {
  font-size: 0.7rem;
  opacity: 0.7;
}

.archive-list {
  margin: 0;
  padding: 0 0 12px;
  list-style: none;
}

.archive-list-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 7px 0 7px 20px;
  border-top: 1px dashed #e9ecef;
  font-size: 0.8rem;
}

.archive-list-email {
  color: #2f3640;
  overflow-wrap: anywhere;
}

.archive-list-meta {
  flex-shrink: 0;
  color: #8b929b;
  white-space: nowrap;
}

/* Bulk archive modal */

.bulk-archive-mode {
  display: flex;
  gap: 12px;
  margin: 14px 0;
  flex-wrap: wrap;
}

.bulk-archive-mode-option {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  flex: 1 1 240px;
  padding: 12px 14px;
  border: 1px solid #d7dbe0;
  border-radius: 10px;
  background-color: #fff;
  cursor: pointer;
  transition:
    border-color 0.2s,
    background-color 0.2s;
}

.bulk-archive-mode-option.is-selected {
  border-color: #d69e2e;
  background-color: #fdf6e7;
}

.bulk-archive-mode-option i {
  margin-top: 3px;
  color: #9aa2ac;
}

.bulk-archive-mode-option.is-selected i {
  color: #d69e2e;
}

.bulk-archive-mode-option span {
  display: flex;
  flex-direction: column;
  font-size: 0.88rem;
}

.bulk-archive-mode-option small {
  color: #7a828c;
  font-size: 0.78rem;
}

.bulk-archive-file {
  display: block;
  margin: 6px 0;
  font-size: 0.85rem;
}

.bulk-archive-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
}

/* .show-form-button carries a 20px top margin for the page's actions row,
   which would push it out of line with its neighbours here. */
.bulk-archive-actions .show-form-button {
  margin-top: 0;
  margin-right: 0;
}

.bulk-archive-actions .show-form-button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* Export is a secondary action; keep it away from the two that write. */
.bulk-archive-export {
  margin-left: auto;
}

.bulk-archive-results {
  margin-top: 16px;
}

.bulk-archive-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 8px 0 12px;
}

.summary-chip {
  display: inline-block;
  padding: 2px 10px;
  border: 1px solid #dfe3e8;
  border-radius: 20px;
  background-color: #f3f5f7;
  color: #5c6470;
  font-size: 0.75rem;
  white-space: nowrap;
}

/* Only the outcomes that need attention get colour. Everything else stays
   grey so a roster full of "nothing to do" rows does not read as noise. */
.summary-chip--actionable {
  border-color: #edd9a3;
  background-color: #fdf6e7;
  color: #8a5a10;
}

.summary-chip--blocked,
.summary-chip--invalid {
  border-color: #f0c2c2;
  background-color: #fdeaea;
  color: #9b2c2c;
}

.bulk-archive-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
}

.bulk-archive-table th {
  padding: 6px 8px;
  border-bottom: 1px solid #dfe3e8;
  color: #7a828c;
  font-weight: 600;
  text-align: left;
}

.bulk-archive-table td {
  padding: 7px 8px;
  border-bottom: 1px dashed #eceff2;
  vertical-align: top;
  overflow-wrap: anywhere;
}

.bulk-archive-table .row-drifted {
  background-color: #fffaf0;
}

.drift-note {
  margin-top: 3px;
  font-size: 0.75rem;
  color: #8a4b10;
}

.drift-note i {
  margin-right: 4px;
}

@media (max-width: 640px) {
  .archive-card-body {
    flex-wrap: wrap;
  }

  .archive-button {
    width: 100%;
    justify-content: center;
  }

  .archive-list-row {
    flex-direction: column;
    gap: 2px;
  }
}

.overlay,
.overlay-content {
  max-width: 95vw;
  overflow-x: hidden;
}

.overlay-content {
  width: min(640px, 90vw);
}

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 80%;
  max-width: 600px;
  margin-left: 250px;
  margin-top: 2%;
  background-color: rgba(0, 0, 0, 0.5); /* Semi-transparent background */
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 10px;
}

.overlay-content {
  background-color: #fff;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  width: 400px; /* Set a fixed width for the form */
  height: 100%;
  overflow-y: auto;
}

.overlay-content-old {
  background: white;
  padding: 20px;
  border-radius: 8px;
  width: 400px;
  max-height: 80vh; /* Limit height to 80% of viewport */
  overflow-y: auto;
  position: relative;
  margin: auto; /* Center the content */
}

.close-button {
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
}

.button-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}
.button-container button {
  margin-left: 10px; /* Add left margin to space out the buttons */
}

.button-container .submit-button {
  background-color: #007bff; /* Blue background */
  color: white;
  border: none;
  border-radius: 4px; /* Rounded corners */
  padding: 10px 20px;
  cursor: pointer;
}

.button-container .submit-button:hover {
  background-color: #0056b3;
}

.button-container .cancel-button {
  background: none;
  border: none; /* Remove border */
  color: #007bff;
  cursor: pointer;
}

.button-container .cancel-button:hover {
  color: #0056b3;
}

.button-container .submit-button:disabled {
  background-color: rgba(
    211,
    211,
    211,
    0.5
  ); /* Light grey semi-transparent background */
  cursor: not-allowed; /* Change cursor to not-allowed */
}

.form-header {
  margin-bottom: 10px;
}

.user-type-header {
  background-color: rgba(
    173,
    216,
    230,
    0.5
  ); /* Light blue, semi-transparent background */
  padding: 10px;
  border-radius: 4px;
  margin-top: 20px;
  margin-bottom: 10px;
}

.form-group {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 10px;
  width: 100%;
}

.form-group label {
  margin-bottom: 5px;
}

.group-select-wrapper {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-bottom: 4px;
}

.field-hint {
  display: block;
  margin-top: 4px;
  font-size: 0.75rem;
  color: #888;
}

.form-group input {
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 15px;
}
.form-group select {
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 15px;
}
.checkbox-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
}

.actions {
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 20px;
}

.search-input {
  padding: 8px;
  margin-right: 20px;
  border: 1px solid #ccc;
  border-radius: 15px;
  width: 40%;
  flex: none;
}

.sort-dropdown {
  padding: 8px;
  margin-right: 10px;
  margin-left: 10ox;
  border: 1px solid #ccc;
  border-radius: 15px;
}

.data-table-container {
  width: 100%;
  max-height: 400px; /* Set a maximum height for the table container */
  /* overflow-y: auto;  Enable vertical scrolling */
  margin-top: 20px; /* Add margin to ensure it is below the header */
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  border: none;
}

.data-table th,
.data-table td {
  border: none; /* Remove border from table cells */
  padding: 8px;
  text-align: left;
  vertical-align: top;
}

.data-table th {
  background-color: transparent;
}

.highlight {
  background-color: #d3eaff;
}
.action-button {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--primary-color);
  font-size: 16px;
  margin-right: 10px;
}
.action-button:hover {
  color: var(--primary-color);
}

.back-link {
  display: block;
  margin-bottom: 10px;
  color: var(--primary-color);
  cursor: pointer;
  text-decoration: none;
}

.back-link:hover {
  color: #0056b3;
}
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
}

.pagination button {
  padding: 8px 12px;
  margin: 0 5px;
  border: none;
  border-radius: 4px;
  background-color: var(--primary-color);
  color: white;
  cursor: pointer;
}

.pagination button:disabled {
  background-color: rgba(211, 211, 211, 0.5);
  cursor: not-allowed;
}

.pagination span {
  margin: 0 10px;
}

.group-opt {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  column-gap: 12px;
  row-gap: 10px;
  align-items: start;
  justify-items: start;
}

.opt-label {
  display: flex;
  align-items: flex-start;
  font-size: 13px;
  gap: 3px;
  width: auto;
}

.opt-label-bulk {
  display: flex;
  align-items: flex-start;
  font-size: 13px;
  gap: 3px;
  margin: 0;
  cursor: pointer;
}

.opt-label--singleline {
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.opt-label--singleline .opt-text {
  white-space: nowrap;
}

.opt-text {
  font-size: 14px;
  line-height: 1.4;
  white-space: normal;
}

.opt-checkbox {
  margin-right: 1px;
}

.opt-checkbox:disabled {
  cursor: not-allowed;
  accent-color: #bbb;
}

.opt-label:has(.opt-checkbox:disabled) {
  color: #999;
}

.opt-checkbox:disabled + .opt-text {
  color: #777;
}

.opt-checkbox:disabled {
  border: 2px solid #ccc;
  background-color: #f5f5f5;
}

.overlay-content {
  background: white;
  padding: 20px;
  border-radius: 8px;
  width: 400px;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
}

.bulk-upload-overlay {
  background: white;
  padding: 20px;
  border-radius: 8px;
  width: 700px;
  max-width: 90vw;
  max-height: 100vh;
  overflow-y: auto;
  position: relative;
  margin: auto; /* Center the content */
}

.bulk-upload-content {
  width: 600px;
  max-height: 85vh;
}

.upload-section {
  margin: 20px 0;
}

.mi-list {
  list-style: none;
  padding-left: 0;
  margin: 10px 0 0;
}

.mi-list li {
  margin: 6px 0;
  line-height: 1.3;
}

.mi-note {
  color: #555;
  font-size: 12px;
  margin: 6px 0;
}

.mi-badge {
  display: inline-block;
  font-size: 11px;
  line-height: 1;
  padding: 3px 6px;
  border-radius: 6px;
  background: #eef2ff;
  color: #3730a3;
  margin-right: 8px;
  vertical-align: text-top;
}

.mi-badge--req {
  background: #fee2e2;
  color: #991b1b;
}

.upload-instructions code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", monospace;
  font-size: 12px;
  background: #f6f8fa;
  padding: 1px 4px;
  border-radius: 4px;
}

.file-upload-container {
  position: relative;
  margin: 20px 0;
}

.file-input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.file-upload-label {
  display: inline-block;
  padding: 12px 20px;
  background-color: #007bff;
  color: white;
  border-radius: 5px;
  cursor: pointer;
  border: 2px dashed #007bff;
  text-align: center;
  transition: all 0.3s ease;
  width: 100%;
  box-sizing: border-box;
}

.file-upload-label:hover {
  background-color: #0056b3;
  border-color: #0056b3;
}

.file-upload-label i {
  margin-right: 8px;
}

.preview-section {
  margin: 20px 0;
}

.preview-section h3 {
  color: #333;
  margin-bottom: 10px;
}

.preview-table-container {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #ddd;
  border-radius: 5px;
}

.preview-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.preview-table th,
.preview-table td {
  padding: 8px;
  text-align: left;
  border-bottom: 1px solid #ddd;
}

.preview-table th {
  background-color: #f8f9fa;
  font-weight: bold;
  position: sticky;
  top: 0;
}

.preview-info {
  margin-top: 10px;
  font-weight: bold;
  color: #007bff;
}

.success-message {
  background-color: #d4edda;
  color: #155724;
  padding: 10px;
  border-radius: 5px;
  margin: 10px 0;
  border: 1px solid #c3e6cb;
  white-space: pre-wrap;
}

.submit-button:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

.fa-spinner {
  animation: spin 1s linear infinite;
}

.confirm-overlay-content {
  width: 420px; /* slightly wider than the default form */
}

.confirm-instructions {
  margin: 10px 0 6px;
  font-weight: 600;
}

.confirm-input {
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 10px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@media (max-width: 480px) {
  .bulk-upload-overlay {
    width: 98vw;
    max-height: 95vh;
    padding: 10px;
  }

  .file-upload-label {
    padding: 10px 15px;
    font-size: 14px;
  }

  .opt-label--singleline,
  .opt-label--singleline .opt-text {
    white-space: normal;
  }
}

@media (max-width: 768px) {
  .bulk-upload-overlay {
    width: 95vw;
    max-width: 95vw;
    max-height: 90vh;
    padding: 15px;
  }
  .bulk-upload-content {
    width: 95%;
    max-width: 95vw;
  }

  .preview-table {
    font-size: 10px;
  }

  .upload-instructions {
    padding: 10px;
  }
  .upload-instructions p {
    font-size: 13px;
  }
  .user-type-header {
    width: fit-content;
  }
  .button-container {
    display: flex;
    justify-content: flex-start;
    margin-top: 20px;
  }
  .button-container button {
    margin-left: 10px; /* Add left margin to space out the buttons */
  }
  .form-group {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: left;
    margin-bottom: 10px;
    width: 100%;
  }
  .form-group label {
    margin-bottom: 5px;
  }
  .form-group input {
    padding: 8px;
    border: 1px solid #ccc;
    border-radius: 15px;
    align-self: start;
  }
  .group-opt {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }
  .checkbox-group {
    display: flex;
    flex-direction: column;
    margin-bottom: 10px;
  }
  .overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    max-width: fit-content;
    height: 90%;
    margin-left: 25px;
    margin-right: 0%;
    margin-top: 2%;
    background-color: rgba(0, 0, 0, 0.5); /* Semi-transparent background */
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border-radius: 10px;
    box-sizing: border-box;
  }
  .overlay-content {
    width: 90%;
    max-width: 100vw;
    height: 100%;
    padding: 20px;
    box-sizing: border-box;
  }

  .data-table-container {
    width: 100%;
    overflow-x: auto; /* Enable horizontal scrolling */
  }
  .data-table {
    width: 50%;
    max-width: 50%;
    border-collapse: collapse;
    border: none;
    overflow-x: auto;
  }
  .data-table th,
  .data-table td {
    white-space: nowrap; /* Prevent text from wrapping */
  }

  .show-form-button {
    padding: 8px 15px;
  }

  .search-input {
    width: 50%;
    margin-bottom: 10px;
  }

  .sort-dropdown {
    width: 50%;
  }

  .content {
    width: 100%;
    height: 100%;
    padding: 0px;
    box-sizing: border-box;
    box-shadow: none;
    background-color: #e9e5d6;
  }
  .new-main-page {
    display: flex;
    flex-direction: column;
    height: 100vh;
    background: #e9e5d6;
  }
  .new-main-header {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 5%;
    background-color: #e9e5d6;
    padding: 5;
    margin: 5;
    margin-top: 20px;
  }
  .new-main-page h1,
  .new-main-page p,
  .new-main-page form,
  .new-main-page div {
    width: 75vw;
    text-align: left;
  }

  .header {
    height: auto;
  }
  .header-image {
    width: 100%;
    height: auto;
  }
}
</style>
